/**
 * 1.4.1: 订阅与套餐标签页组件
 * 账号中心的订阅与套餐 Tab，封装 SubscriptionCard 组件
 * 遵循 YUI 主题设计规范
 * 1.4.2: 添加用户角色检查，member 不显示升级按钮
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrganizationSubscription } from '../../services/billingService'; // 1.4.5: 使用组织ID获取订阅
import { getCurrentOrganization } from '../../services/organizationService';
import { isSupabaseAvailable } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { SubscriptionCard } from '../SubscriptionCard';
import type { SubscriptionInfo } from '../../types/billing';
import type { MemberRole } from '../../types/organization';

export function SubscriptionTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<MemberRole | null>(null);
  
  useEffect(() => {
    loadSubscriptionInfo();
  }, []);
  
  const loadSubscriptionInfo = async () => {
    if (!isSupabaseAvailable) {
      setIsLoading(false);
      return;
    }
    
    try {
      // 1.4.5: 先获取当前组织，再用组织ID获取订阅信息
      const orgResult = await getCurrentOrganization();
      
      // 1.4.2: 获取用户在当前组织中的角色
      if (orgResult.organization) {
        setUserRole(orgResult.organization.role);
        
        // 1.4.5: 使用组织ID获取订阅，确保member看到正确的订阅状态
        const subResult = await getOrganizationSubscription(orgResult.organization.id);
        if (subResult.error) {
          logger.error('Error loading subscription:', subResult.error);
        } else {
          setSubscription(subResult.subscription);
        }
      }
    } catch (error) {
      logger.error('Error loading subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpgrade = () => {
    navigate('/upgrade');
  };
  
  // 1.4.2: 只有 owner 可以升级账号
  const canUpgrade = userRole === 'owner';
  
  return (
    <div className="space-y-6">
      <SubscriptionCard
        subscription={subscription}
        isLoading={isLoading}
        onUpgrade={canUpgrade ? handleUpgrade : undefined}
        showUpgradeButton={canUpgrade}
        onSubscriptionUpdate={loadSubscriptionInfo}
      />
    </div>
  );
}
